/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionCollapseAll';

const ActionCollapseAllManager: ActionManagerFn<'ActionCollapseAll'> = (
  platform: Platform,
): ActionManager<'ActionCollapseAll'> => {
  return {
    ...baseActionManager(platform, 'ActionCollapseAll'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Target Grid Item ID',
        type: 'ItemID',
        required: true,
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'TreeColumn') {
            return `Item id '${value}' doesn't belong to a TreeColumn`;
          }
          return '';
        },
      },
    },
    name: 'Collapse All',
    icon: { iconName: 'down-left-and-up-right-to-center' },
    description: `**Overview**<br/>
The Collapse All action enables users to programmatically close every expanded parent row within a specific TreeColumn component. This utility is the functional inverse of "Expand All," allowing users to instantly revert a complex, multi-level hierarchy into a compact, top-level summary view.

**Key Features**<br/>
* **Global Collapse:** Triggers the closing of all currently open nested nodes and child items within the target tree hierarchy, returning the grid to its most compact state.
* **Strict Component Validation:** Includes built-in safeguards to ensure the action is only linked to valid TreeColumn components, preventing configuration errors during the build process.
* **Hierarchy Reset:** Effective for quickly resetting the visual state of deep data structures to their root level.

**Properties**<br/>
* **Target Grid Item ID (Required):** The unique identifier of the specific component to control.
  * *Constraint:* The target must be a valid TreeColumn type. If a non-tree component is selected, the system will prevent configuration and display a validation error.

**Use Cases**<br/>
* **Data Overview:** Quickly hide detailed information to provide a high-level summary of the root categories.
* **View Reset:** Allow users to easily return the grid to its initial state after deep exploration.
* **Focus Management:** Declutter the interface by hiding irrelevant nested data when moving between major data categories.`,
  };
};

export default ActionCollapseAllManager;
